
import { Component, Vue, Mixins, Prop } from 'vue-property-decorator'
import MixinChart from '../../mixins/Chart.vue'
import { Dataset, Options } from '../../@types/Slide'
import {
  RadialChartOptions,
  ChartDataSets,
  ChartOptions,
  RadialLinearScale,
  helpers
} from 'chart.js'

@Component
export default class Radar extends Mixins<Chart>(MixinChart) {
  /**
   * Chart type.
   */
  public readonly chartType: string = 'radar'

  /**
   * List of plugins.
   */
  public plugins = [
    {
      beforeDraw(chart) {
        const { groups } = chart.chart.options
        if (groups) {
          const ctx: CanvasRenderingContext2D = chart.chart.ctx
          const nbLabels = chart.scale.pointLabels.length
          const angle = i => ((360 / nbLabels) * i - 90) * (Math.PI / 180)

          for (let i = 0; i < groups.length; i++) {
            ctx.strokeStyle = groups[i].color || 'gray-800'
            ctx.lineWidth = groups[i].weight || 4
            const indexes = groups[i].data.sort((a, b) => a - b)
            const { x, y } = chart.scale.getPointPositionForValue(
              indexes[0],
              chart.scale.max
            )
            ctx.beginPath()
            ctx.arc(
              chart.scale.xCenter,
              chart.scale.yCenter,
              chart.scale.drawingArea,
              angle(indexes[0]),
              angle(indexes[indexes.length - 1])
            )
            ctx.stroke()

            if (groups[i].title) {
              const fontSize = 30
              let lines = Array.isArray(groups[i].title.text)
                ? groups[i].title.text.length
                : 1
              ctx.fillStyle =
                groups[i].title.color || groups[i].color || 'gray-800'
              ctx.font = fontSize + 'px Poppins'
              const avg = indexes.reduce((t, a, i, f) => t + a / f.length, 0)
              const { x: avgX, y: avgY } = chart.scale.getPointPositionForValue(
                avg,
                chart.scale.max
              )
              let { x: marginX = 0, y: marginY = 0 } = groups[i].title
              if (marginX.toString().includes('%'))
                marginX = (parseFloat(marginX) / 100) * chart.scale.drawingArea
              if (marginY.toString().includes('%'))
                marginY = (parseFloat(marginY) / 100) * chart.scale.drawingArea
              const posX = Number(marginX) + avgX
              let posY = 0

              if (lines > 1) {
                groups[i].title.text.forEach((title, i) => {
                  posY = Number(marginY) + avgY + fontSize * i
                  ctx.fillText(title, posX, posY)
                })
              } else {
                posY = Number(marginY) + avgY
                ctx.fillText(groups[i].title.text, posX, posY)
              }
            }
          }
        }
      }
    }
  ]
  /**
   * Global default options.
   */
  public readonly defaultDatasetOptions: ChartDataSets = {
    pointRadius: 8,
    pointHoverRadius: 15
  }

  /**
   * Global default options.
   */
  public readonly defaultChartOptions: ChartOptions | RadialChartOptions = {
    legend: {
      position: 'left',
      labels: {
        fontColor: 'black'
      }
    },

    scale: {
      pointLabels: {
        fontSize: 20,
        fontColor: 'black'
      },
      ticks: {
        beginAtZero: true,
        fontColor: 'black'
      },

      xAxes: [
        {
          gridLines: {
            lineWidth: 3
          }
        }
      ],

      yAxes: [
        {
          gridLines: {
            lineWidth: 3
          }
        }
      ],
      gridLines: {
        //@ts-ignore
        circular: true,
        lineWidth: 3
      }
    },

    tooltips: {
      enabled: true,
      callbacks: {
        title([{ datasetIndex, index }], { datasets }) {
          const dataset = datasets[datasetIndex]
          if (this._chart.options.labels) {
            return `${this._chart.options.labels[index]}`
          } else {
            return `${dataset.label}`
          }
        },
        label({ datasetIndex, index }, { datasets }) {
          const dataset = datasets[datasetIndex]
          return `${dataset.label}: ${dataset.data[index]}`
        }
      }
    }
  }
}
